import React, { useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { IFacility } from "api/rpc/2022-09/clientAdmin/facility/facility";

import { filterHistoryAddItem, filterHistoryCreate } from "redux/actions/filters/filterHistory";
import { IOrder, OrderFinancialStatus } from "redux/reducers/models/order";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { valueToString } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";

import Avatar from "components/avatar/Avatar";
import { Badge } from "components/badge/Badge";
import Icon from "components/icon/Icon";
import DataTable from "components/dataTable/DataTable";
import { faCheck, faCircleExclamation, faCircleXmark, faTurnDownLeft } from "@fortawesome/pro-regular-svg-icons";

import "./ordersTable.scss";

type TOrdersTableProps = {
  /** Passing undefined triggers Spin component */
  orders: IOrder[];
  facilities?: IFacility[];
  tableLimit: number;
  tableOffset: number;
  handleTableOffset: (direction: "prev" | "next") => void;
  handleTableNavigation?: (order_id: number) => void;
  noClick?: boolean;
  facilityName?: boolean;
};

const OFFSET_HISTORY_KEY = "facility_orders_offset";

export default function OrdersTable(props: TOrdersTableProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation<{ params: { orderId: number } }>();

  const dispatch = useAppDispatch();
  const filterHistoryStore = useAppSelector(store => store.filterHistoryStore);

  useEffect(() => {
    if (filterHistoryStore[OFFSET_HISTORY_KEY] === undefined) {
      dispatch(filterHistoryCreate(OFFSET_HISTORY_KEY));
    }
  }, []);

  function navigateToItem(id: number) {
    if (props.handleTableNavigation) {
      props.handleTableNavigation(id);
      return;
    }

    dispatch(filterHistoryAddItem(OFFSET_HISTORY_KEY, props.tableOffset));
    history.push("/admin/order/" + String(id));
  }

  const elementScrollRef = useCallback(element => {
    const locationState = location.state;
    if (element !== null && locationState?.params?.orderId) {
      if (element?.id === locationState?.params?.orderId) {
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, []);

  function getFacilityName(facilityId: number) {
    const facilityName = props.facilities.find(facility => facility.id === facilityId).full_name;
    return facilityName;
  }

  return (
    <DataTable
      className="order-table"
      columns={
        props.facilities || props.facilityName
          ? [
            { label: t("Order No.") },
            { label: t("Date") },
            { label: t("Customer") },
            { label: t("Facility") },
            { label: t("Status") },
            { label: t("Purchase") },
          ]
          : [
            { label: t("Order No.") },
            { label: t("Date") },
            { label: t("Customer") },
            { label: t("Status") },
            { label: t("Purchase") },
          ]
      }
      loading={props.orders === undefined}
      footer={{
        tableLimit: props.tableLimit,
        tableOffset: props.tableOffset,
        handleTableOffset: direction => props.handleTableOffset(direction),
        disableNextOffset: !props.orders || !(props.orders?.length === props.tableLimit),
      }}
    >
      {props.orders?.map(order => (
        <tr
          key={order.id}
          id={String(order.id)}
          className={props.noClick ? "" : "clickable"}
          onClick={() => (props.noClick ? null : navigateToItem(order.id))}
          ref={elementScrollRef}
        >
          <td>{`# ${order.number}`}</td>
          <td>
            {order.processed_at ? (
              <>
                <span>{moment.utc(order.processed_at).local().format("MMM DD, YYYY")} at </span>
                <span>{moment.utc(order.processed_at).local().format("h:mm A")}</span>
              </>
            ) : (
              <>
                <span>{moment.utc(order.created_at).local().format("MMM DD, YYYY")} at </span>
                <span>{moment.utc(order.created_at).local().format("h:mm A")}</span>
              </>
            )}
          </td>
          <td className="table-data-customer">
            <div className="wrap">
              {order.customer ? (
                <>
                  <Avatar size="medium" source={order.customer?.profile_image_source} />
                  <div className="customer">
                    <span>{order.customer?.full_name}</span>
                    <span>{order.customer?.email}</span>
                  </div>
                </>
              ) : (
                <div className="customer" />
              )}
            </div>
          </td>
          {props.facilities && <td>{getFacilityName(order.facility_id)}</td>}
          {props.facilityName && <td>{order.facility_name}</td>}
          <td>{generateOrderStatusBadge(order.financial_status)}</td>
          <td>
            <LocaleCurrency currency={order.currency} amount={order.total_price} />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

/** Generate order status badges from financial_status title.
 *   -  Easily adjust badge styling in 1 place going forward
 *   -  TODO: Translations
 */
export function generateOrderStatusBadge(status: OrderFinancialStatus): JSX.Element {
  switch (status) {
    case "paid":
      return (
        <Badge
          type="success"
          // className="paid-order-badge"
          iconLeft={<Icon icon={faCheck} size="small" />}
        >
          {valueToString(status, "capitalize")}
        </Badge>
      );
    case "partially_paid":
      return (
        <Badge
          type="primary"
          // className="partial_paid-order-badge"
          iconLeft={<Icon icon={faCircleExclamation} size="small" />}
        >
          {valueToString(status, "capitalize")}
        </Badge>
      );
    case "cancelled":
      return (
        <Badge
          type="error"
          // className="cancelled-order-badge"
          iconLeft={<Icon icon={faCircleXmark} size="small" />}
        >
          {valueToString(status, "capitalize")}
        </Badge>
      );

    case "refunded":
      return (
        <Badge
          type="warning"
          // className="refunded-order-badge"
          iconLeft={<Icon icon={faTurnDownLeft} size="small" />}
        >
          {valueToString(status, "capitalize")}
        </Badge>
      );

    default:
      return status ? (
        <Badge type="gray" className="sales-channel-badge">
          {valueToString(status, "capitalize")}
        </Badge>
      ) : null;
  }
}
